import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, UserPlus } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AdminTable } from "@/features/admin/components/AdminTable";
import { AdminUserCard } from "@/features/admin/components/AdminUserCard";
import { BanUserDialog } from "@/features/admin/components/BanUserDialog";
import { CreateTestAccountDialog } from "@/features/admin/components/CreateTestAccountDialog";
import { DeductCreditsDialog } from "@/features/admin/components/DeductCreditsDialog";
import { DeleteAccountDialog } from "@/features/admin/components/DeleteAccountDialog";
import { GrantCreditsDialog } from "@/features/admin/components/GrantCreditsDialog";
import { UnbanUserDialog } from "@/features/admin/components/UnbanUserDialog";
import { ChangeTierDialog } from "@/features/admin/components/ChangeTierDialog";
import {
  banUser,
  createTestAccount,
  deleteAccountImmediately,
  deductCredits,
  grantCredits,
  loadAdminUserAiAbuseReviews,
  loadAdminUserDetail,
  loadAdminUsersPage,
  updateUserTier,
  unbanUser,
} from "@/features/admin/api/adminClient";
import type { AdminAiAbuseReview, AdminUsersFilters } from "@/features/admin/admin.types";

const defaultFilters: AdminUsersFilters = {
  page: 1,
  searchText: "",
  statusFilter: "all",
  tierFilter: "all",
};

type WorkspaceMode = "users" | "credits" | "bans";

export function AdminUserOperationsWorkspace({ mode }: { mode: WorkspaceMode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedUserId = searchParams.get("userId");
  const [filters, setFilters] = useState<AdminUsersFilters>(defaultFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [abuseLoading, setAbuseLoading] = useState(false);
  const [abuseError, setAbuseError] = useState<string | null>(null);
  const [abuseReviews, setAbuseReviews] = useState<AdminAiAbuseReview[]>([]);
  const [pageData, setPageData] = useState<Awaited<ReturnType<typeof loadAdminUsersPage>> | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof loadAdminUserDetail>> | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [deductDialogOpen, setDeductDialogOpen] = useState(false);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [unbanDialogOpen, setUnbanDialogOpen] = useState(false);
  const [changeTierDialogOpen, setChangeTierDialogOpen] = useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [mutationBusy, setMutationBusy] = useState(false);
  const copy = {
    actionLabel: t(`adminWorkspace.modes.${mode}.actionLabel`),
    body: t(`adminWorkspace.modes.${mode}.body`),
    eyebrow: t(`adminWorkspace.modes.${mode}.eyebrow`),
    title: t(`adminWorkspace.modes.${mode}.title`),
  };

  const summary = useMemo(() => {
    if (!pageData) {
      return t("adminWorkspace.common.loadingResults");
    }

    return t("adminWorkspace.modes.pageSummary", {
      page: pageData.page,
      pageCount: pageData.pageCount,
      totalCount: pageData.totalCount,
    });
  }, [pageData, t]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void loadAdminUsersPage(filters)
      .then((result) => {
        if (cancelled) {
          return;
        }

        setPageData(result);
        setSelectedUserId((current) => {
          if (focusedUserId) {
            return focusedUserId;
          }

          return result.items.some((item) => item.userId === current)
            ? current
            : result.items[0]?.userId ?? null;
        });
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }

        setError(loadError instanceof Error ? loadError.message : t("adminWorkspace.errors.loadUsers"));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters, focusedUserId, t]);

  useEffect(() => {
    if (focusedUserId) {
      setSelectedUserId(focusedUserId);
    }
  }, [focusedUserId]);

  useEffect(() => {
    if (!selectedUserId) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);

    void loadAdminUserDetail(selectedUserId)
      .then((result) => {
        if (!cancelled) {
          setDetail(result);
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          setDetailError(loadError instanceof Error ? loadError.message : t("adminWorkspace.errors.loadDetail"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedUserId, t]);

  useEffect(() => {
    if (!selectedUserId) {
      setAbuseReviews([]);
      setAbuseError(null);
      setAbuseLoading(false);
      return;
    }

    let cancelled = false;
    setAbuseLoading(true);
    setAbuseError(null);

    void loadAdminUserAiAbuseReviews(selectedUserId)
      .then((result) => {
        if (!cancelled) {
          setAbuseReviews(result);
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          setAbuseError(loadError instanceof Error ? loadError.message : t("adminWorkspace.errors.loadDetail"));
          setAbuseReviews([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAbuseLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedUserId, t]);

  async function refreshCurrentSelection(userId = selectedUserId) {
    const nextPage = await loadAdminUsersPage(filters);
    setPageData(nextPage);

    if (userId) {
      setSelectedUserId(userId);
      const [nextDetail, nextAbuseReviews] = await Promise.all([
        loadAdminUserDetail(userId),
        loadAdminUserAiAbuseReviews(userId),
      ]);
      setDetail(nextDetail);
      setAbuseReviews(nextAbuseReviews);
      setAbuseError(null);
    }
  }

  function handleSelectUser(userId: string) {
    if (searchParams.has("userId")) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete("userId");
      setSearchParams(nextSearchParams, { replace: true });
    }

    setSelectedUserId(userId);
  }

  async function runMutation(action: () => Promise<void>) {
    setMutationBusy(true);
    setError(null);

    try {
      await action();
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : t("adminWorkspace.errors.actionFailed"));
    } finally {
      setMutationBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <section className="rounded-[1.7rem] border border-white/8 bg-white/[0.035] px-5 py-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.34em] text-[#b69d7e]">
              {copy.eyebrow}
            </p>
            <h2 className="mt-3 font-display text-4xl text-[#fff7ef]">{copy.title}</h2>
            <p className="mt-3 text-sm leading-7 text-[#d7cab9]/76">{copy.body}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[280px]">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#9d886d]" />
              <Input
                className="border-white/10 bg-black/20 pl-11 text-[#fff7ef] placeholder:text-[#8f7a63]"
                placeholder={t("adminWorkspace.common.searchByNameOrEmail")}
                value={filters.searchText}
                onChange={(event) => setFilters((current) => ({ ...current, page: 1, searchText: event.target.value }))}
              />
            </div>

            <select
              aria-label={t("adminWorkspace.common.filterStatus")}
              className="rounded-full border border-white/10 bg-black/20 px-4 py-3 text-sm text-[#fff7ef] outline-none transition focus:border-[#d2b793]/40 focus:ring-2 focus:ring-[#d2b793]/20"
              value={filters.statusFilter}
              onChange={(event) => setFilters((current) => ({
                ...current,
                page: 1,
                statusFilter: event.target.value as AdminUsersFilters["statusFilter"],
              }))}
            >
              <option value="all">{t("adminWorkspace.common.allStatuses")}</option>
              <option value="active">{t("adminWorkspace.statuses.active")}</option>
              <option value="banned">{t("adminWorkspace.statuses.banned")}</option>
              <option value="pending_deletion">{t("adminWorkspace.statuses.pending_deletion")}</option>
              <option value="deleted">{t("adminWorkspace.statuses.deleted")}</option>
            </select>

            <select
              aria-label={t("adminWorkspace.common.filterTier")}
              className="rounded-full border border-white/10 bg-black/20 px-4 py-3 text-sm text-[#fff7ef] outline-none transition focus:border-[#d2b793]/40 focus:ring-2 focus:ring-[#d2b793]/20"
              value={filters.tierFilter}
              onChange={(event) => setFilters((current) => ({
                ...current,
                page: 1,
                tierFilter: event.target.value as AdminUsersFilters["tierFilter"],
              }))}
            >
              <option value="all">{t("adminWorkspace.common.allTiers")}</option>
              <option value="free">{t("common.tiers.free")}</option>
              <option value="crafter">{t("common.tiers.crafter")}</option>
              <option value="author">{t("common.tiers.author")}</option>
            </select>

            {mode === "users" ? (
              <Button type="button" onClick={() => setCreateDialogOpen(true)}>
                <UserPlus className="size-4" />
                {copy.actionLabel}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mt-4 text-sm text-[#d7cab9]/62">{summary}</div>
      </section>

      {error ? (
        <div className="rounded-[1.4rem] border border-[#7b3430]/35 bg-[#6d2d29]/25 px-5 py-4 text-sm text-[#f6d8d2]">
          {error}
        </div>
      ) : null}

      <section className="grid gap-5 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]">
        <div className="space-y-4">
          <AdminTable
            items={pageData?.items ?? []}
            loading={loading}
            selectedUserId={selectedUserId}
            onSelectUser={handleSelectUser}
          />

          <div className="flex items-center justify-between gap-3 rounded-[1.5rem] border border-white/8 bg-black/20 px-5 py-4">
            <Button
              disabled={(pageData?.page ?? 1) <= 1 || loading}
              type="button"
              variant="outline"
              onClick={() => setFilters((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
            >
              {t("adminWorkspace.common.previous")}
            </Button>
            <span className="text-sm text-[#d7cab9]/70">{summary}</span>
            <Button
              disabled={!pageData || pageData.page >= pageData.pageCount || loading}
              type="button"
              variant="outline"
              onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
            >
              {t("adminWorkspace.common.next")}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <AdminUserCard
            detail={detail}
            error={detailError}
            loading={detailLoading}
            mode={mode}
            onBan={() => setBanDialogOpen(true)}
            onChangeTier={() => setChangeTierDialogOpen(true)}
            onDeductCredits={() => setDeductDialogOpen(true)}
            onDeleteAccount={() => setDeleteAccountDialogOpen(true)}
            onGrantCredits={() => setGrantDialogOpen(true)}
            onOpenPqr={(ticketRef) => navigate(`/admin/pqr?ticketRef=${encodeURIComponent(ticketRef)}`)}
            onUnban={() => setUnbanDialogOpen(true)}
          />

          <section className="space-y-3 rounded-[1.6rem] border border-white/10 bg-black/20 p-5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-[0.22em] text-[#b69d7e]">
                {t("adminWorkspace.aiAbuse.title", { defaultValue: "AI abuse reviews" })}
              </h3>
              <p className="text-xs text-[#d8c8b3]/65">
                {abuseLoading
                  ? t("adminWorkspace.aiAbuse.loading", { defaultValue: "Loading..." })
                  : t("adminWorkspace.aiAbuse.count", {
                    count: abuseReviews.length,
                    defaultValue: `${abuseReviews.length} entries`,
                  })}
              </p>
            </div>

            {abuseError ? (
              <div className="rounded-[1.2rem] border border-[#7b3430]/35 bg-[#6d2d29]/25 px-4 py-3 text-sm text-[#f6d8d2]">
                {abuseError}
              </div>
            ) : null}

            {!abuseError && !abuseLoading && abuseReviews.length === 0 ? (
              <div className="rounded-[1.2rem] border border-dashed border-white/8 px-4 py-6 text-sm text-[#d7cab9]/62">
                {t("adminWorkspace.aiAbuse.empty", { defaultValue: "No review entries for this user." })}
              </div>
            ) : null}

            <div className="space-y-2">
              {abuseReviews.map((review) => (
                <article className="rounded-[1.2rem] border border-white/8 bg-white/[0.03] px-4 py-3" key={review.id}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{review.tierSnapshot}</Badge>
                      <Badge variant="outline">
                        {t(`adminWorkspace.aiAbuse.status.${review.status}`, {
                          defaultValue: review.status,
                        })}
                      </Badge>
                    </div>
                    <span className="text-xs text-[#d8c8b3]/65">
                      {new Intl.DateTimeFormat(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      }).format(new Date(review.detectedAt))}
                    </span>
                  </div>

                  <p className="mt-2 text-sm text-[#fff7ef]">
                    {t("adminWorkspace.aiAbuse.summary", {
                      average: review.tierAverage24hRequests.toFixed(2),
                      defaultValue:
                        `Observed ${review.observed24hRequests} requests in 24h (${review.usageMultiplier.toFixed(2)}x of tier average ${review.tierAverage24hRequests.toFixed(2)}).`,
                      observed: review.observed24hRequests,
                      usageMultiplier: review.usageMultiplier.toFixed(2),
                    })}
                  </p>
                </article>
              ))}
            </div>
          </section>
        </div>
      </section>

      <CreateTestAccountDialog
        busy={mutationBusy}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={(input) => {
          void runMutation(async () => {
            const result = await createTestAccount(input);
            setCreateDialogOpen(false);
            await refreshCurrentSelection(result.userId);
          });
        }}
      />

      <GrantCreditsDialog
        busy={mutationBusy}
        open={grantDialogOpen}
        onOpenChange={setGrantDialogOpen}
        onSubmit={(input) => {
          if (!selectedUserId) {
            return;
          }

          void runMutation(async () => {
            await grantCredits({ ...input, userId: selectedUserId });
            setGrantDialogOpen(false);
            await refreshCurrentSelection(selectedUserId);
          });
        }}
      />

      <DeductCreditsDialog
        busy={mutationBusy}
        open={deductDialogOpen}
        onOpenChange={setDeductDialogOpen}
        onSubmit={(input) => {
          if (!selectedUserId) {
            return;
          }

          void runMutation(async () => {
            await deductCredits({ ...input, userId: selectedUserId });
            setDeductDialogOpen(false);
            await refreshCurrentSelection(selectedUserId);
          });
        }}
      />

      <BanUserDialog
        busy={mutationBusy}
        open={banDialogOpen}
        onOpenChange={setBanDialogOpen}
        onSubmit={(input) => {
          if (!selectedUserId) {
            return;
          }

          void runMutation(async () => {
            await banUser({ ...input, userId: selectedUserId });
            setBanDialogOpen(false);
            await refreshCurrentSelection(selectedUserId);
          });
        }}
      />

      <UnbanUserDialog
        busy={mutationBusy}
        open={unbanDialogOpen}
        onOpenChange={setUnbanDialogOpen}
        onSubmit={(input) => {
          if (!selectedUserId) {
            return;
          }

          void runMutation(async () => {
            await unbanUser({ ...input, userId: selectedUserId });
            setUnbanDialogOpen(false);
            await refreshCurrentSelection(selectedUserId);
          });
        }}
      />

      <ChangeTierDialog
        busy={mutationBusy}
        currentTier={detail?.tier ?? "free"}
        open={changeTierDialogOpen}
        onOpenChange={setChangeTierDialogOpen}
        onSubmit={(input) => {
          if (!selectedUserId) {
            return;
          }

          void runMutation(async () => {
            await updateUserTier({ ...input, userId: selectedUserId });
            setChangeTierDialogOpen(false);
            await refreshCurrentSelection(selectedUserId);
          });
        }}
      />
      <DeleteAccountDialog
        busy={mutationBusy}
        open={deleteAccountDialogOpen}
        onOpenChange={setDeleteAccountDialogOpen}
        onSubmit={(input) => {
          if (!selectedUserId) {
            return;
          }

          void runMutation(async () => {
            await deleteAccountImmediately({ ...input, userId: selectedUserId });
            setDeleteAccountDialogOpen(false);
            await refreshCurrentSelection(selectedUserId);
          });
        }}
      />
    </div>
  );
}

export function AdminUsersPage() {
  return <AdminUserOperationsWorkspace mode="users" />;
}
